const initQuantity = (root) => {
  root.querySelectorAll('[data-pfs-quantity]:not([data-pfs-ready])').forEach((quantity) => {
    quantity.dataset.pfsReady = 'true';
    const input = quantity.querySelector('input');
    if (!input) return;

    quantity.querySelectorAll('button').forEach((button) => {
      button.addEventListener('click', () => {
        const step = Number(input.step) || 1;
        const minimum = Number(input.min) || 0;
        const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
        const direction = button.dataset.quantityAction === 'increase' ? 1 : -1;
        const next = Math.min(maximum, Math.max(minimum, Number(input.value || minimum) + direction * step));
        input.value = String(next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  });
};

const CART_LINES_UPDATE_EVENT = 'shopify:cart:lines-update';
const addButtonResetTimers = new WeakMap();

const renderCartCount = (count, animate = true) => {
  if (!Number.isFinite(Number(count))) return;

  const nextCount = String(Number(count));
  document.querySelectorAll('[data-pfs-cart-count]').forEach((element) => {
    element.textContent = nextCount;
  });

  document.querySelectorAll('[data-pfs-cart-link]').forEach((link) => {
    const labelTemplate = link.dataset.cartLabelTemplate;
    if (labelTemplate) link.setAttribute('aria-label', labelTemplate.replace('__COUNT__', nextCount));
    if (!animate) return;
    link.classList.remove('is-cart-updated');
    requestAnimationFrame(() => link.classList.add('is-cart-updated'));
    window.setTimeout(() => link.classList.remove('is-cart-updated'), 1200);
  });
};

const setAddButtonState = (button, state, label) => {
  const labelElement = button.querySelector('[data-pfs-add-label]');
  if (!labelElement) return;

  if (!button.dataset.pfsOriginalLabel) button.dataset.pfsOriginalLabel = labelElement.textContent.trim();
  const existingTimer = addButtonResetTimers.get(button);
  if (existingTimer) window.clearTimeout(existingTimer);

  button.dataset.pfsState = state;
  button.toggleAttribute('aria-busy', state === 'adding');
  button.disabled = state === 'adding';
  labelElement.textContent = label;

  if (state === 'adding') return;

  const resetTimer = window.setTimeout(() => {
    labelElement.textContent = button.dataset.pfsOriginalLabel || labelElement.textContent;
    delete button.dataset.pfsState;
    button.removeAttribute('aria-busy');
    button.disabled = !button.form?.querySelector('input[name="id"]')?.value;
    addButtonResetTimers.delete(button);
  }, state === 'added' ? 2800 : 3600);

  addButtonResetTimers.set(button, resetTimer);
};

const renderAddFeedback = (feedback, state, count) => {
  if (!feedback) return;

  const mark = feedback.querySelector('[data-pfs-cart-feedback-mark]');
  const title = feedback.querySelector('[data-pfs-cart-feedback-title]');
  const detail = feedback.querySelector('[data-pfs-cart-feedback-detail]');
  const link = feedback.querySelector('[data-pfs-cart-feedback-link]');
  const countTemplate = feedback.dataset.countTemplate || '';

  feedback.hidden = false;
  feedback.dataset.state = state;
  if (mark) mark.textContent = state === 'error' ? '!' : state === 'adding' ? '…' : '✓';
  if (title) {
    title.textContent =
      state === 'error'
        ? feedback.dataset.errorLabel || ''
        : state === 'adding'
          ? feedback.dataset.addingLabel || ''
          : feedback.dataset.addedLabel || '';
  }
  if (detail) {
    detail.textContent = state === 'added' ? countTemplate.replace('__COUNT__', String(count)) : '';
  }
  if (link) link.hidden = state !== 'added';
};

const initCartEvents = () => {
  if (document.documentElement.dataset.pfsCartEventsReady === 'true') return;
  document.documentElement.dataset.pfsCartEventsReady = 'true';

  document.addEventListener(CART_LINES_UPDATE_EVENT, (event) => {
    if (event.action !== 'add' || !event.promise) return;

    const productForm = event.target instanceof Element ? event.target.closest('product-form-component') : null;
    if (!productForm) return;

    const buttons = Array.from(productForm.querySelectorAll('[data-pfs-add], [data-pfs-flow-switch-add]'));
    const feedback = productForm.querySelector('[data-pfs-cart-feedback]');
    const addingLabel = feedback?.dataset.addingLabel || '';
    buttons.forEach((button) => setAddButtonState(button, 'adding', addingLabel));
    renderAddFeedback(feedback, 'adding', 0);

    event.promise
      .then(({ cart, detail }) => {
        if (detail?.didError) throw new Error('Cart add failed');
        const count = cart?.totalQuantity ?? detail?.itemCount;
        const addedLabel = feedback?.dataset.addedLabel || '';
        buttons.forEach((button) => setAddButtonState(button, 'added', addedLabel));
        renderAddFeedback(feedback, 'added', count);
        renderCartCount(count);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        const errorLabel = feedback?.dataset.errorLabel || '';
        buttons.forEach((button) => setAddButtonState(button, 'error', errorLabel));
        renderAddFeedback(feedback, 'error', 0);
      });
  });
};

const initCart = (root) => {
  root.querySelectorAll('[data-pfs-cart]:not([data-pfs-ready])').forEach((cartRoot) => {
    cartRoot.dataset.pfsReady = 'true';
    const form = cartRoot.querySelector('#PfsCartForm');
    if (!form) return;

    let updateTimer;

    const setStatus = (message) => {
      const status = cartRoot.querySelector('[data-pfs-cart-status]');
      if (status) status.textContent = message;
    };

    const updateLine = async ({ key, quantity, input, focusAction }) => {
      cartRoot.setAttribute('aria-busy', 'true');
      setStatus(cartRoot.dataset.updatingMessage || '');

      try {
        const response = await fetch(Theme.routes.cart_change_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: key,
            quantity,
            sections: cartRoot.dataset.sectionId,
            sections_url: window.location.pathname,
          }),
        });
        const cart = await response.json();
        if (!response.ok || cart.errors) throw new Error(cart.errors || `Cart update failed with status ${response.status}`);

        renderCartCount(cart.item_count);
        const sectionMarkup = cart.sections?.[cartRoot.dataset.sectionId];
        if (!sectionMarkup) throw new Error('Updated cart section is missing');

        const responseDocument = new DOMParser().parseFromString(sectionMarkup, 'text/html');
        const nextCart = responseDocument.querySelector('[data-pfs-cart]');
        if (!nextCart) throw new Error('Updated cart markup is incomplete');

        cartRoot.replaceWith(nextCart);
        initPureFlow(nextCart.parentElement || document);

        if (quantity === 0) {
          const nextRemove = nextCart.querySelector('[data-pfs-cart-remove]');
          const nextHeading = nextCart.querySelector('h1');
          if (nextRemove) {
            nextRemove.focus();
          } else if (nextHeading) {
            nextHeading.tabIndex = -1;
            nextHeading.focus();
          }
          return;
        }

        const nextInput = Array.from(nextCart.querySelectorAll('[data-pfs-cart-quantity]')).find(
          (candidate) => candidate.dataset.cartKey === key
        );
        const nextFocus =
          focusAction === 'input'
            ? nextInput
            : nextInput?.closest('[data-pfs-quantity]')?.querySelector(`[data-quantity-action="${focusAction}"]`);
        nextFocus?.focus();
      } catch (error) {
        console.error('Unable to update cart.', error);
        cartRoot.removeAttribute('aria-busy');
        if (input) input.value = input.defaultValue;
        setStatus(cartRoot.dataset.errorMessage || '');
      }
    };

    const queueUpdate = (input, focusAction = 'input', delay = 260) => {
      if (!input.checkValidity()) {
        input.reportValidity();
        return;
      }

      window.clearTimeout(updateTimer);
      updateTimer = window.setTimeout(
        () =>
          updateLine({
            key: input.dataset.cartKey,
            quantity: Number(input.value),
            input,
            focusAction,
          }),
        delay
      );
    };

    form.addEventListener('change', (event) => {
      const input = event.target.closest('[data-pfs-cart-quantity]');
      if (!input) return;
      const activeAction = document.activeElement?.dataset?.quantityAction || 'input';
      queueUpdate(input, activeAction);
    });

    form.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-pfs-cart-remove]');
      if (!remove) return;
      event.preventDefault();
      window.clearTimeout(updateTimer);
      updateLine({ key: remove.dataset.cartKey, quantity: 0, focusAction: 'remove' });
    });
  });
};

const initProduct = (root) => {
  root.querySelectorAll('[data-pfs-product]:not([data-pfs-ready])').forEach((productRoot) => {
    productRoot.dataset.pfsReady = 'true';
    let abortController;

    const updateVariant = async (selectedOption) => {
      const form = productRoot.querySelector('[data-pfs-product-form]');
      const sectionId = productRoot.dataset.sectionId;
      const productUrl = selectedOption.dataset.productUrl || productRoot.dataset.productUrl;
      if (!form || !sectionId || !productUrl) return;

      const selectedOptionValueIds = Array.from(form.querySelectorAll('[data-pfs-option]:checked'))
        .map((input) => input.dataset.optionValueId)
        .filter(Boolean);
      if (!selectedOptionValueIds.length) return;

      abortController?.abort();
      const requestController = new AbortController();
      abortController = requestController;
      const requestUrl = new URL(productUrl, window.location.origin);
      requestUrl.searchParams.set('section_id', sectionId);
      requestUrl.searchParams.set('option_values', selectedOptionValueIds.join(','));
      productRoot.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(requestUrl, { signal: requestController.signal });
        if (!response.ok) throw new Error(`Variant request failed with status ${response.status}`);

        const responseText = await response.text();
        const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
        const nextProductRoot = responseDocument.querySelector('[data-pfs-product]');
        const currentDetails = productRoot.querySelector('.pfs-product-details');
        const nextDetails = nextProductRoot?.querySelector('.pfs-product-details');
        if (!nextProductRoot || !currentDetails || !nextDetails) throw new Error('Variant response is incomplete');

        const currentMedia = productRoot.querySelector('.pfs-product-media');
        const nextMedia = nextProductRoot.querySelector('.pfs-product-media');
        currentDetails.replaceWith(nextDetails);
        if (currentMedia && nextMedia) currentMedia.replaceWith(nextMedia);

        productRoot.dataset.productUrl = nextProductRoot.dataset.productUrl || productUrl;
        initQuantity(productRoot);

        const variantId = nextDetails.querySelector('input[name="id"]')?.value;
        const historyUrl = new URL(productRoot.dataset.productUrl, window.location.origin);
        if (variantId) {
          historyUrl.searchParams.set('variant', variantId);
        } else {
          historyUrl.searchParams.set('option_values', selectedOptionValueIds.join(','));
        }
        window.history.replaceState({}, '', historyUrl);

        const selectedOptionValueId = selectedOption.dataset.optionValueId;
        const nextSelectedOption = Array.from(productRoot.querySelectorAll('[data-pfs-option]')).find(
          (input) => input.dataset.optionValueId === selectedOptionValueId
        );
        nextSelectedOption?.focus();
      } catch (error) {
        if (error.name !== 'AbortError') console.error('Unable to update product variant.', error);
      } finally {
        if (abortController === requestController) productRoot.removeAttribute('aria-busy');
      }
    };

    productRoot.addEventListener('change', (event) => {
      const selectedOption = event.target.closest('[data-pfs-option]');
      if (selectedOption) updateVariant(selectedOption);
    });

    productRoot.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-pfs-thumb]');
      if (!thumb) return;

      const mainImage = productRoot.querySelector('[data-pfs-main-image]');
      if (!mainImage) return;
      mainImage.src = thumb.dataset.imageSrc || '';
      mainImage.srcset = thumb.dataset.imageSrcset || '';
      productRoot.querySelectorAll('[data-pfs-thumb]').forEach((item) => item.setAttribute('aria-current', 'false'));
      thumb.setAttribute('aria-current', 'true');
    });
  });
};

const initCollection = (root) => {
  root.querySelectorAll('[data-pfs-sort]:not([data-pfs-ready])').forEach((select) => {
    select.dataset.pfsReady = 'true';
    select.addEventListener('change', () => select.form?.submit());
  });
};

const initMenus = (root) => {
  root.querySelectorAll('.pfs-mobile-menu:not([data-pfs-ready])').forEach((details) => {
    details.dataset.pfsReady = 'true';
    details.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        details.open = false;
        details.querySelector('summary')?.focus();
      }
    });
    details.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => {
        details.open = false;
      });
    });
  });
};

const initPureFlow = (root = document) => {
  initQuantity(root);
  initCart(root);
  initProduct(root);
  initCollection(root);
  initMenus(root);
  initCartEvents();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initPureFlow());
} else {
  initPureFlow();
}

document.addEventListener('shopify:section:load', (event) => initPureFlow(event.target));
